import React, { createContext, useContext, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchEmployeeDetails, resetEmployeeDetailsState } from '../store/slices/employeeSlice';

interface EmployeeContextValue {
  employeeDetails: Record<string, any>;
  loadingEmployee: boolean;
  employeeError: string | null;
  refetchEmployee: () => void;
  resetEmployee: () => void;
  hasEmployeeData: boolean;
}

const EmployeeContext = createContext<EmployeeContextValue | undefined>(undefined);

interface EmployeeProviderProps {
  children: ReactNode;
}

/**
 * EmployeeProvider component that manages employee details state and provides it to the application.
 * This provider fetches employee details once on mount and makes it available to all child components.
 * This prevents multiple API calls when multiple components need employee data.
 * 
 * @example
 * <EmployeeProvider>
 *   <App />
 * </EmployeeProvider>
 */
export const EmployeeProvider: React.FC<EmployeeProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const employeeDetails = useAppSelector((state) => state.employee.employeeDetails);
  const loadingEmployee = useAppSelector((state) => state.employee.loading);
  const employeeError = useAppSelector((state) => state.employee.error);

  const hasEmployeeData = useMemo(
    () => Object.keys(employeeDetails).length > 0,
    [employeeDetails]
  );

  const refetchEmployee = useCallback(() => {
    dispatch(fetchEmployeeDetails());
  }, [dispatch]);

  const resetEmployee = useCallback(() => {
    dispatch(resetEmployeeDetailsState());
  }, [dispatch]);

  // Auto-fetch employee details on mount if not already loaded and not currently loading
  useEffect(() => {
    if (!hasEmployeeData && !loadingEmployee && !employeeError) {
      refetchEmployee();
    }
  }, []); // Empty deps - only run once on mount

  const value = useMemo(() => ({
    employeeDetails,
    loadingEmployee,
    employeeError,
    refetchEmployee,
    resetEmployee,
    hasEmployeeData,
  }), [employeeDetails, loadingEmployee, employeeError, refetchEmployee, resetEmployee, hasEmployeeData]);

  return <EmployeeContext.Provider value={value}>{children}</EmployeeContext.Provider>;
};

/**
 * Custom hook to access employee context.
 * Must be used within an EmployeeProvider.
 * 
 * @throws {Error} If used outside EmployeeProvider
 * @returns {EmployeeContextValue} The employee context value containing employee data, loading state, error, and utility functions
 * 
 * @example
 * const { employeeDetails, loadingEmployee, employeeError, refetchEmployee } = useEmployee();
 */
export const useEmployee = (): EmployeeContextValue => {
  const context = useContext(EmployeeContext);
  
  if (context === undefined) {
    throw new Error('useEmployee must be used within an EmployeeProvider');
  }
  
  return context;
};
